import React from "react";
import { motion } from "framer-motion";
import { Handshake, BadgePercent, Lock, Users, CalendarCheck, Sparkles, Briefcase, Bot } from "lucide-react";

const PerkCard = ({ icon: Icon, title, subtitle, description, gradient, accentColor, delay, badge }) => (
  <motion.div
    initial={{ opacity: 0, y: 25 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ delay, duration: 0.5 }}
    className="rounded-[24px] overflow-hidden border border-white shadow-[0_20px_60px_-16px_rgba(0,0,0,0.08)] bg-white/80 backdrop-blur-xl relative h-full flex flex-col"
  >
    {/* Top accent */}
    <div className="absolute top-0 left-0 right-0 h-[4px]" style={{ background: gradient }} />

    {badge && (
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        whileInView={{ opacity: 1, scale: 1 }}
        transition={{ delay: delay + 0.3, type: "spring", stiffness: 300 }}
        className="absolute top-6 right-6"
      >
        <div
          className="px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider text-white shadow-sm"
          style={{ background: gradient }}
        >
          {badge}
        </div>
      </motion.div>
    )}

    <div className="p-5 flex-1 flex flex-col">
      <div
        className="w-11 h-11 rounded-2xl flex items-center justify-center mb-4 shadow-lg border border-white/20"
        style={{ background: gradient }}
      >
        <Icon className="w-5 h-5 text-white" />
      </div>

      <h3 className="text-[17px] font-bold text-slate-900 mb-1.5">{title}</h3>
      {subtitle && (
        <p className="text-[14px] font-extrabold mb-4 uppercase tracking-wide" style={{ color: accentColor }}>
          {subtitle}
        </p>
      )}
      <p className="text-[13px] text-slate-500 leading-relaxed font-medium">{description}</p>
    </div>
  </motion.div>
);

const FellowsPartnerPerksSlide = () => {
  return (
    <div className="h-full w-full bg-[#f8fafc] flex flex-col items-center justify-center p-6 xl:p-8 relative overflow-hidden font-sans">
      {/* Ambient gradient blurs — blue scheme */}
      <div
        className="absolute top-[-200px] right-[-100px] w-[700px] h-[700px] rounded-full blur-[140px] opacity-25 pointer-events-none"
        style={{ background: "linear-gradient(135deg, #93c5fd 0%, #c4b5fd 100%)" }}
      />
      <div
        className="absolute bottom-[-200px] left-[-100px] w-[600px] h-[600px] rounded-full blur-[120px] opacity-20 pointer-events-none"
        style={{ background: "linear-gradient(135deg, #a5f3fc 0%, #bfdbfe 100%)" }}
      />
      <div className="absolute inset-0 bg-[radial-gradient(#cbd5e1_0.8px,transparent_0.8px)] [background-size:28px_28px] opacity-40 pointer-events-none" />

      <div className="w-full flex flex-col items-center relative z-10 max-w-[1200px]">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-6"
        >
          <h2 className="text-3xl lg:text-[46px] font-extrabold text-slate-900 tracking-tight leading-tight">
            <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              Program Perks
            </span>
          </h2>
        </motion.div>

        {/* Perk cards — top row of 3, bottom row of 2 centered */}
        <div className="w-full grid grid-cols-2 gap-5 mb-5">
          <PerkCard
            icon={Handshake}
            title="Asha Dentist Partner"
            description="Earn ongoing revenue share for every practice you help bring onboard to Asha. Your influence creates income."
            gradient="linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)"
            accentColor="#2563eb"
            delay={0.2}
            badge="Revenue Share"
          />

          <PerkCard
            icon={Sparkles}
            title="Thought Leader Spotlight"
            description="Be spotlighted as a thought leader in AI in dentistry. We promote your work across our platform and community."
            gradient="linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)"
            accentColor="#0284c7"
            delay={0.4}
            badge="Visibility"
          />
        </div>
        <div className="w-full grid grid-cols-2 gap-5">
          <PerkCard
            icon={Briefcase}
            title="Consulting Services"
            description="List your own AI consulting packages on the Asha Marketplace. We promote them to our growing network of practices — you keep 85% of revenue."
            gradient="linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)"
            accentColor="#6d28d9"
            delay={0.5}
            badge="Advisory"
          />

          <PerkCard
            icon={Bot}
            title="Agent Marketplace"
            description="Build and publish your own AI agents using our Clinician Creator Suite. Free or paid — for paid agents, you keep 70% of all revenue."
            gradient="linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)"
            accentColor="#0d9488"
            delay={0.6}
            badge="Coming Soon"
          />
        </div>

        {/* Bottom summary bar */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.5 }}
          className="mt-5 w-full rounded-2xl overflow-hidden border border-blue-100 shadow-sm bg-blue-50/50 backdrop-blur-sm max-w-[1000px]"
        >
          <div className="grid grid-cols-3 divide-x divide-blue-100/50">
            {[
              {
                icon: Lock,
                text: "These benefits are not available outside the Fellows program",
              },
              {
                icon: Users,
                text: "Limited cohort sizes to ensure meaningful mentorship",
              },
              {
                icon: CalendarCheck,
                text: "Apply during onboarding — decisions within 2 weeks",
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ delay: 0.8 + i * 0.1, duration: 0.4 }}
                className="flex items-center gap-3 px-6 py-4"
              >
                <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center shrink-0 shadow-sm border border-blue-50">
                  <item.icon className="w-4 h-4 text-blue-500" />
                </div>
                <span className="text-[13px] text-slate-600 font-semibold leading-snug">{item.text}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default FellowsPartnerPerksSlide;
